import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";
import { useSSEListener, SSE_RECONNECTED } from "../SSE/SSEContext";

/**
 * DeployRefreshManager — deploy-warning countdown + stale-tab refresh banner.
 * Plan: BDR_Mobile plans/impl_deploy_refresh_warning_2026_07_22.md (v2).
 *
 * Two lanes, ONE state machine (idle → countdown → waitingHealthy →
 * manualFallback; graceCountdown feeds the same waitingHealthy):
 *
 * 1. SSE lane: ops/blue-green-deploy.sh fires `deploy_notice` ~30s before the
 *    pm2 promote. Banner counts down, then polls /version.json (an express
 *    route reporting the RUNNING process's boot-time build id — deliberately
 *    not the on-disk file, which flips minutes early at git reset) and
 *    reloads when the served id differs from this bundle's baked id.
 * 2. Fallback lane (missed the SSE — asleep laptop, evicted tab): version
 *    check on mount, every 5 min, on window focus, and on SSE_RECONNECTED.
 *    Mismatch → banner with a 60s grace countdown + "Refresh now" button →
 *    same wait-healthy reload.
 *
 * Evicted tabs (past the 3-connection SSE cap) get the notice relayed over a
 * BroadcastChannel from any connected sibling tab, so the 30s warning reaches
 * every same-origin tab, not just the three with sockets.
 *
 * All deadlines are wall-clock (Date.now() vs deadline), never tick-counted —
 * background-tab timer throttling only delays the display, not correctness.
 *
 * Mounted for ANY logged-in user (adminIsLoggedIn is a logged-in check, not a
 * role check): refinishers get the fallback lane; their SSE lane is inert
 * because SSEContext only opens sockets for admin/office.
 */

// Baked at CI build time (REACT_APP_BUILD_ID). Absent (local dev, pre-CI
// builds) => the whole manager is inert.
const RUNNING_BUILD_ID = process.env.REACT_APP_BUILD_ID || null;

const CHANNEL_NAME = "bdr-deploy-notice";
const VERSION_POLL_MS = 5 * 60 * 1000; // fallback lane cadence
const HEALTHY_POLL_MS = 2000; // post-countdown "is the new process up" cadence
const WAIT_HEALTHY_TIMEOUT_MS = 3 * 60 * 1000; // then flip to manual banner
const GRACE_MS = 60 * 1000; // fallback lane auto-reload grace
const RELOAD_STAMP_KEY = "bdr_deploy_auto_reload_at";
const RELOAD_LOOP_WINDOW_MS = 10 * 60 * 1000; // suppress auto-reload re-fire

// Fetch the RUNNING server's build id. null => unknown/disabled (never
// triggers a banner); string => compare against RUNNING_BUILD_ID.
async function fetchServedBuildId() {
  try {
    const res = await fetch(`/version.json?t=${Date.now()}`, {
      cache: "no-store",
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data && data.buildId ? String(data.buildId) : null;
  } catch (_e) {
    return null; // offline / mid-restart — try again next tick
  }
}

// Stamp IMMEDIATELY before every auto reload so a post-reload mismatch (e.g. a
// misbehaving cache serving a stale index.html) degrades to the manual banner
// instead of a reload loop.
function autoReloadNow() {
  try {
    sessionStorage.setItem(RELOAD_STAMP_KEY, String(Date.now()));
  } catch (_e) {
    /* storage unavailable — reload anyway */
  }
  window.location.reload();
}

function recentlyAutoReloaded() {
  try {
    const at = parseInt(sessionStorage.getItem(RELOAD_STAMP_KEY), 10);
    return Boolean(at) && Date.now() - at < RELOAD_LOOP_WINDOW_MS;
  } catch (_e) {
    return false;
  }
}

export default function DeployRefreshManager() {
  // phase: "idle" | "countdown" | "grace" | "waitingHealthy" | "manualFallback"
  const [phase, setPhase] = useState("idle");
  const [remainingSec, setRemainingSec] = useState(null);
  const phaseRef = useRef(phase);
  phaseRef.current = phase;
  const deadlineRef = useRef(null);
  const waitStartedRef = useRef(null);
  const channelRef = useRef(null);

  // ---- shared: enter waitingHealthy, poll for the new process, reload -------
  const beginWaitHealthy = useCallback(() => {
    setPhase("waitingHealthy");
    waitStartedRef.current = Date.now();
  }, []);

  useEffect(() => {
    if (phase !== "waitingHealthy") return undefined;
    let stopped = false;
    const poll = async () => {
      if (stopped) return;
      if (Date.now() - waitStartedRef.current > WAIT_HEALTHY_TIMEOUT_MS) {
        setPhase("manualFallback");
        return;
      }
      const served = await fetchServedBuildId();
      if (stopped) return;
      if (served && RUNNING_BUILD_ID && served !== RUNNING_BUILD_ID) {
        if (recentlyAutoReloaded()) {
          setPhase("manualFallback"); // loop guard: something re-served the old bundle
          return;
        }
        autoReloadNow();
        return;
      }
      setTimeout(poll, HEALTHY_POLL_MS);
    };
    poll();
    return () => {
      stopped = true;
    };
  }, [phase]);

  // ---- countdown/grace ticker (display only — deadline math is authoritative)
  useEffect(() => {
    if (phase !== "countdown" && phase !== "grace") return undefined;
    const tick = () => {
      const left = Math.max(
        0,
        Math.ceil((deadlineRef.current - Date.now()) / 1000)
      );
      setRemainingSec(left);
      if (left <= 0 && phaseRef.current === (phase)) {
        beginWaitHealthy();
      }
    };
    tick();
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
  }, [phase, beginWaitHealthy]);

  // ---- SSE lane: deploy_notice starts/RESETS the countdown ------------------
  const startCountdown = useCallback((seconds) => {
    const s = Number.isFinite(seconds) && seconds > 0 ? seconds : 30;
    deadlineRef.current = Date.now() + s * 1000;
    // A repeat notice (script rerun) RESETS the same countdown — never a
    // second poll loop; waitingHealthy in progress is left alone.
    if (phaseRef.current === "idle" || phaseRef.current === "countdown" || phaseRef.current === "grace") {
      setPhase("countdown");
    }
  }, []);

  const onDeployNotice = useCallback(
    (event) => {
      if (!RUNNING_BUILD_ID) return; // dev build — inert
      startCountdown(event && event.secondsUntilRestart);
      // Relay to same-origin sibling tabs beyond the SSE connection cap.
      try {
        channelRef.current?.postMessage({
          kind: "deploy_notice",
          secondsUntilRestart: event && event.secondsUntilRestart,
        });
      } catch (_e) {
        /* channel closed — siblings fall back to the version poll */
      }
    },
    [startCountdown]
  );
  useSSEListener("deploy_notice", onDeployNotice);

  // BroadcastChannel receive side (evicted tabs).
  useEffect(() => {
    if (!RUNNING_BUILD_ID || typeof BroadcastChannel === "undefined") {
      return undefined;
    }
    const ch = new BroadcastChannel(CHANNEL_NAME);
    channelRef.current = ch;
    ch.onmessage = (msg) => {
      if (msg?.data?.kind === "deploy_notice") {
        startCountdown(msg.data.secondsUntilRestart);
      }
    };
    return () => {
      channelRef.current = null;
      ch.close();
    };
  }, [startCountdown]);

  // ---- fallback lane: version poll ------------------------------------------
  const checkVersion = useCallback(async () => {
    if (!RUNNING_BUILD_ID) return;
    if (phaseRef.current !== "idle") return; // countdown/wait already active
    const served = await fetchServedBuildId();
    if (!served || served === RUNNING_BUILD_ID) return;
    if (phaseRef.current !== "idle") return; // re-check after await
    if (recentlyAutoReloaded()) {
      setPhase("manualFallback");
      return;
    }
    deadlineRef.current = Date.now() + GRACE_MS;
    setPhase("grace");
  }, []);

  useEffect(() => {
    if (!RUNNING_BUILD_ID) return undefined;
    checkVersion(); // on mount (also dedupes SSE_RECONNECTED's first-connect fire)
    const id = setInterval(checkVersion, VERSION_POLL_MS);
    const onFocus = () => checkVersion();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(id);
      window.removeEventListener("focus", onFocus);
    };
  }, [checkVersion]);

  const onReconnected = useCallback(() => {
    // Post-restart reconnect lands seconds after promote — catch missed
    // notices fast instead of waiting for the 5-min tick.
    checkVersion();
  }, [checkVersion]);
  useSSEListener(SSE_RECONNECTED, onReconnected);

  // ---- render ---------------------------------------------------------------
  if (!RUNNING_BUILD_ID || phase === "idle") return null;

  let text;
  let showButton = false;
  if (phase === "countdown") {
    text = `Server update in ${remainingSec ?? "…"}s — save your work now.`;
  } else if (phase === "grace") {
    text = `A new version is available — refreshing in ${remainingSec ?? "…"}s.`;
    showButton = true;
  } else if (phase === "waitingHealthy") {
    text = "Updating — the page will refresh automatically…";
  } else {
    text = "A new version is available.";
    showButton = true;
  }

  const banner = (
    // bdr-grad-container-heavy + bdr-shadow-depth: same purple->blue gradient
    // and depth glow the app's sticky headers use, so the banner reads as part
    // of the shell rather than a foreign toast. Edge-to-edge bar, so only a
    // bottom border (std 2px white) instead of the full bdr-border-std box.
    <div
      className="bdr-grad-container-heavy bdr-shadow-depth"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        // Above every modal layer (GlobalConfirm 1000002, CycleConfirmationModal
        // 1000003) but below GlobalSnackbar (1100000/1100001).
        zIndex: 1050000,
        color: "#fff",
        borderBottom: "2px solid #fff",
        padding: "10px 16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 14,
        fontWeight: 600,
        fontSize: 14,
      }}
    >
      <span>{text}</span>
      {showButton && (
        <button
          // Immediate reload: the grace banner only shows once the NEW process
          // is already serving (that's what tripped the mismatch), and an
          // explicit user click must never be deferred by the loop guard.
          onClick={() => window.location.reload()}
          style={{
            background: "#fff",
            color: "#1a3a75",
            border: "none",
            padding: "5px 14px",
            borderRadius: 5,
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          Refresh now
        </button>
      )}
    </div>
  );

  return ReactDOM.createPortal(banner, document.body);
}
